import type { FetchKey } from '@app/src/api/fetchHooks'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { isEmptyEquivalentValue } from '@app/src/utils/emptyEquivalentValues'
import type { IntlShape } from 'react-intl'
import type { FilterOption, MrtColumnFilter, MrtFilterOperator } from './mrtCollectionTypes'

/**
 * Pure `toFilterGroups` builders shared by the entity column registries. Each turns a filter editor's
 * value + chosen operator into the backend FilterGroup[] for a field.
 */

// The valueless empty/notEmpty operators filter on presence alone.
const presenceGroup = (field: string, operator: MrtFilterOperator, intersect: boolean): FilterGroup => ({
  name: field,
  filters: [{ operator: operator === 'empty' ? Operators.IsEmpty : Operators.IsNotEmpty }],
  ...(intersect && { intersect: true }),
})

/** Multi-select → `field In [values]` ("is") or `NotIn` ("isNot"); empty/notEmpty become presence
 * checks. `asNumber` casts option values to numbers; `intersect` makes each active group its own
 * EXISTS/.Any() check (needed when several groups hit the same collection). */
export const multiSelectToIn =
  (field: string, asNumber: boolean, intersect = false) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    if (operator === 'empty' || operator === 'notEmpty') return [presenceGroup(field, operator, intersect)]

    const entries = Array.isArray(value) ? value : []
    const rawValues = entries.map(entry => (typeof entry === 'string' ? entry : (entry as FilterOption).value))
    const values = asNumber ? rawValues.map(Number) : rawValues
    if (!values.length) return []

    const inOperator = operator === 'isNot' ? Operators.NotIn : Operators.In
    return [{ name: field, filters: [{ value: values, operator: inOperator }], ...(intersect && { intersect: true }) }]
  }

/** Every custom category shares the categoryOptions collection, so empty/notEmpty must name the category;
 * on the option id alone they would ask about options in any category. intersect gives each category
 * filter its own Any(); sharing one, no single option could satisfy two categories. */
export const categoryOptionsToFilterGroups =
  (categoryOptionsPath: string, categoryId: number) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    if (operator === 'empty' || operator === 'notEmpty') {
      const equalityOperator = operator === 'empty' ? Operators.NotEqualTo : Operators.EqualTo
      return [
        {
          name: `${categoryOptionsPath}.categoryId`,
          filters: [{ value: categoryId, operator: equalityOperator }],
          intersect: true,
        },
      ]
    }
    return multiSelectToIn(`${categoryOptionsPath}.id`, true, true)(value, operator)
  }

/** Pass enumWithNotSelected for a column whose enum has a "Not selected" member, which the cells show as
 * Empty: empty/notEmpty then also ask whether the field holds it. "notEmpty" lists the other members rather
 * than negating that one: on a collection path a negative group becomes its own !Any() and would reject a
 * row as soon as any element is Not selected. */
export const enumToFilterGroups =
  (field: string, enumWithNotSelected?: Record<string, string>) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    const members = enumWithNotSelected ? Object.values(enumWithNotSelected) : []
    const emptyMember = members.find(isEmptyEquivalentValue)
    if (emptyMember !== undefined && operator === 'empty')
      return [
        {
          name: field,
          filters: [{ operator: Operators.IsEmpty }, { value: emptyMember, operator: Operators.EqualTo }],
        },
      ]
    if (emptyMember !== undefined && operator === 'notEmpty')
      return [
        { name: field, filters: [{ value: members.filter(member => member !== emptyMember), operator: Operators.In }] },
      ]
    return multiSelectToIn(field, false)(value, operator)
  }

const parsePlainNumber = (value: string): number | undefined => (value === '' ? undefined : Number(value))

/** Numeric range → a gte group and/or an lte group. Each bound is its own FilterGroup because the
 * backend OR's sections within a group but AND's separate groups, so "between" must be two groups (both
 * in one group would match everything). `parse` converts each bound (defaults to a plain number); pass a
 * currency-aware parser to accept '200k'/'1.5M' inputs. */
export const rangeToFilterGroups =
  (field: string, parse: (value: string) => number | undefined = parsePlainNumber) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    if (operator === 'empty' || operator === 'notEmpty') return [presenceGroup(field, operator, false)]
    if (!Array.isArray(value)) return []

    const [min, max] = value as [string | undefined, string | undefined]
    const parsedMin = min != null ? parse(min) : undefined
    const parsedMax = max != null ? parse(max) : undefined
    const groups: FilterGroup[] = []
    if (parsedMin !== undefined && !Number.isNaN(parsedMin))
      groups.push({ name: field, filters: [{ value: parsedMin, operator: Operators.GreaterThanOrEqual }] })
    if (parsedMax !== undefined && !Number.isNaN(parsedMax))
      groups.push({ name: field, filters: [{ value: parsedMax, operator: Operators.LowerThanOrEqual }] })
    return groups
  }

/** Date range → a gte group and/or an lte group (see rangeToFilterGroups for why they are separate). */
export const dateRangeToFilterGroups =
  (field: string) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    if (operator === 'empty' || operator === 'notEmpty') return [presenceGroup(field, operator, false)]
    if (!Array.isArray(value)) return []

    const [from, to] = value as [Date | string | undefined, Date | string | undefined]
    const groups: FilterGroup[] = []
    if (from)
      groups.push({
        name: field,
        filters: [{ value: new Date(from).toISOString(), operator: Operators.GreaterThanOrEqual }],
      })
    if (to)
      groups.push({
        name: field,
        filters: [{ value: new Date(to).toISOString(), operator: Operators.LowerThanOrEqual }],
      })
    return groups
  }

/** A date-range column filter on one field. */
export const dateRangeFilter = (field: string): MrtColumnFilter => ({
  field,
  variant: 'date-range',
  toFilterGroups: dateRangeToFilterGroups(field),
})

/** A multi-select column filter backed by a facet option list. */
export const facetMultiSelectFilter = (
  field: string,
  facetName: string,
  asNumber: boolean,
  isEnum?: boolean,
  formatLabel?: (rawLabel: string) => string,
): MrtColumnFilter => ({
  field,
  variant: 'multi-select',
  facet: { name: facetName, isEnum, formatLabel },
  toFilterGroups: multiSelectToIn(field, asNumber),
})

/** Labels one raw enum name for a facet option list, via `<valuesIntlPrefix>.<name>`. The members that
 * mean an absence get the same "Empty" label their cells show, so the filter reads like the column. The
 * option keeps its raw value, so it still filters as `field In ['NotSelected']`. */
export const formatEnumFacetLabel =
  (valuesIntlPrefix: string, formatMessage: IntlShape['formatMessage']) =>
  (raw: string): string =>
    formatMessage({ id: isEmptyEquivalentValue(raw) ? 'general.empty' : `${valuesIntlPrefix}.${raw}` })

/** An enum column's multi-select filter: the facet returns the raw enum names, which are translated for
 * display via `<valuesIntlPrefix>.<name>`. */
export const enumMultiSelectFilter = (
  field: string,
  valuesIntlPrefix: string,
  formatMessage: IntlShape['formatMessage'],
  enumWithNotSelected?: Record<string, string>,
): MrtColumnFilter => ({
  field,
  variant: 'multi-select',
  facet: {
    name: field,
    isEnum: true,
    formatLabel: formatEnumFacetLabel(valuesIntlPrefix, formatMessage),
  },
  toFilterGroups: enumToFilterGroups(field, enumWithNotSelected),
})

type ForeignFacetOptions = {
  endpoint: string
  fetchKey: FetchKey
  isEnum?: boolean
  // For a field holding ids (country.id, industry.id, ...), whose facet returns them as strings.
  asNumber?: boolean
  formatLabel?: (rawLabel: string) => string
}

/** A multi-select filter whose options come from another resource's facet endpoint, for a column reached
 * through a relation. `facetName` is the path as that resource names it, while `field` is the path from
 * this collection's entity. */
export const foreignFacetMultiSelectFilter = (
  field: string,
  facetName: string,
  { endpoint, fetchKey, isEnum, asNumber = false, formatLabel }: ForeignFacetOptions,
): MrtColumnFilter => ({
  field,
  variant: 'multi-select',
  facet: { name: facetName, isEnum, endpoint, fetchKey, formatLabel },
  toFilterGroups: multiSelectToIn(field, asNumber),
})

/** The common global-search shape: match one field with `Contains`, or send nothing for a blank term. */
export const containsSearchFilters =
  (field: string) =>
  (searchTerm: string): FilterGroup[] => {
    const term = searchTerm.trim()
    return term ? [{ name: field, filters: [{ value: term, operator: Operators.Contains }] }] : []
  }
